import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";

const scrollToContact = () => {
  const el = document.querySelector("#contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TIERS = [
  {
    id: "tier-1",
    name: "Notify",
    tagline: "Stop the calls. Start the database.",
    priceNote: "Pricing on request",
    cta: "Get Started Free",
    highlight: false,
    features: [
      "Order-ready WhatsApp sent automatically",
      "Customer database built automatically",
      "Full order and job history",
      "Works on any phone or laptop",
      "Set up in 10 minutes",
    ],
    testId: "pricing-tier-1",
    ctaTestId: "pricing-cta-1",
  },
  {
    id: "tier-2",
    name: "Notify + Market",
    tagline: "Bring customers back. Grow revenue.",
    priceNote: "Pricing on request",
    cta: "Get Started Free",
    highlight: true,
    features: [
      "Everything in Notify",
      "2 marketing broadcasts per month",
      "Send to up to 100 recent customers",
      "Campaign templates for Eid, Ramadan, seasonal offers",
      "In-window promotional messages",
    ],
    testId: "pricing-tier-2",
    ctaTestId: "pricing-cta-2",
  },
  {
    id: "tier-3",
    name: "Managed Service",
    tagline: "We run it for you, end to end.",
    priceNote: "Pricing on request",
    cta: "Contact Us",
    highlight: false,
    features: [
      "Everything in Notify + Market",
      "Ping team writes and manages all campaigns",
      "Monthly performance reporting",
      "Dedicated account manager",
      "Priority support",
    ],
    testId: "pricing-tier-3",
    ctaTestId: "pricing-cta-3",
  },
];

export const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-[#F9FAFB] py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] ping-gradient-text mb-4">
            Pricing
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] font-black tracking-tight text-[#1A1A2E]">
            Simple, transparent <span className="ping-gradient-text">pricing</span>
          </h2>
          <p className="mt-5 text-lg text-gray-600 max-w-2xl">
            No contracts. No hidden fees. First month free.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
          {TIERS.map((tier, i) => (
            <motion.div
              key={tier.id}
              data-testid={tier.testId}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className={`relative rounded-3xl p-7 sm:p-9 flex flex-col ${
                tier.highlight
                  ? "bg-[#1A1A2E] text-white shadow-2xl shadow-[#7B5EA7]/25 lg:scale-[1.03] lg:-translate-y-1 border border-transparent"
                  : "bg-white border border-gray-200/80 hover:border-[#7B5EA7]/30 hover:shadow-xl hover:shadow-gray-200/60 transition-all duration-300"
              }`}
            >
              {tier.highlight && (
                <>
                  <div className="absolute inset-0 rounded-3xl pointer-events-none ping-gradient opacity-100" style={{ padding: "2px", WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)", WebkitMaskComposite: "xor", maskComposite: "exclude" }} />
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 ping-gradient text-white text-[11px] font-bold tracking-wider uppercase px-3.5 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-[#7B5EA7]/40">
                    <Sparkles size={12} strokeWidth={2.5} />
                    Most Popular
                  </div>
                </>
              )}

              <div className="relative">
                <h3
                  className={`text-2xl font-black tracking-tight mb-1.5 ${
                    tier.highlight ? "text-white" : "ping-gradient-text"
                  }`}
                >
                  {tier.name}
                </h3>
                <p
                  className={`text-sm mb-6 ${
                    tier.highlight ? "text-white/70" : "text-gray-500"
                  }`}
                >
                  {tier.tagline}
                </p>

                <div
                  className={`text-lg font-bold mb-7 ${
                    tier.highlight ? "text-white" : "text-[#1A1A2E]"
                  }`}
                >
                  {tier.priceNote}
                </div>

                <ul className="space-y-3.5 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                          tier.highlight
                            ? "bg-white/15"
                            : "ping-gradient"
                        }`}
                      >
                        <Check
                          size={12}
                          className="text-white"
                          strokeWidth={3}
                        />
                      </span>
                      <span
                        className={`text-[15px] leading-snug ${
                          tier.highlight ? "text-white/85" : "text-gray-700"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={tier.ctaTestId}
                  onClick={scrollToContact}
                  className={`w-full mt-auto font-semibold px-6 py-3.5 rounded-full transition-all active:scale-[0.98] ${
                    tier.highlight
                      ? "bg-white text-[#1A1A2E] hover:bg-gray-100 hover:scale-[1.02]"
                      : "ping-gradient text-white hover:shadow-lg hover:shadow-[#7B5EA7]/30 hover:scale-[1.02]"
                  }`}
                >
                  {tier.cta}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center text-sm text-gray-500 max-w-2xl mx-auto"
        >
          Prices vary by vertical. Ping Drive pricing differs from garment and
          pet care verticals.{" "}
          <button
            data-testid="pricing-vertical-link"
            onClick={scrollToContact}
            className="underline hover:text-[#7B5EA7] transition-colors font-medium"
          >
            Contact us for a quote
          </button>{" "}
          specific to your business.
        </motion.p>
      </div>
    </section>
  );
};

export default Pricing;
